import React from "react";
import { BsCheckCircle, BsExclamationTriangle, BsInfoCircle, BsXCircle, BsX } from "react-icons/bs";

interface U2000AlertProps {
  message: string | string[];
  type?: "success" | "error" | "warning" | "info";
  isOpen: boolean;
  onClose?: () => void;
  showIcon?: boolean;
}

// Configuration moderne avec système de design cohérent
const alertConfig = {
  success: {
    containerClass: "bg-green-50 border-green-200 border-l-green-500",
    textClass: "text-green-800",
    iconClass: "text-green-600",
    iconBg: "bg-green-100",
    icon: BsCheckCircle,
  },
  error: {
    containerClass: "bg-red-50 border-red-200 border-l-red-500",
    textClass: "text-red-800",
    iconClass: "text-red-600",
    iconBg: "bg-red-100",
    icon: BsXCircle,
  },
  warning: {
    containerClass: "bg-yellow-50 border-yellow-200 border-l-yellow-500",
    textClass: "text-yellow-800",
    iconClass: "text-yellow-600",
    iconBg: "bg-yellow-100",
    icon: BsExclamationTriangle,
  },
  info: {
    containerClass: "bg-blue-50 border-blue-200 border-l-blue-500",
    textClass: "text-blue-800",
    iconClass: "text-blue-600",
    iconBg: "bg-blue-100",
    icon: BsInfoCircle,
  },
};

const U2000Alert: React.FC<U2000AlertProps> = ({ message, type = "info", onClose, isOpen, showIcon = true }) => {
  if (!isOpen) {
    return null;
  }

  const config = alertConfig[type];
  const IconComponent = config.icon;

  return (
    <div
      className={`relative flex items-start gap-3 border border-l-4 rounded-lg px-4 py-3 shadow-xs 
                 ${config.containerClass} animate-slide-up transition-all duration-200`}
      role="alert"
      aria-live="assertive"
    >
      {/* Icône moderne avec fond circulaire */}
      {showIcon && (
        <div className={`shrink-0 flex items-center justify-center w-6 h-6 rounded-full ${config.iconBg}`}>
          <IconComponent className={`h-4 w-4 ${config.iconClass}`} />
        </div>
      )}

      {/* Contenu du message */}
      <div className={`flex-1 ${config.textClass}`}>
        {Array.isArray(message) ? (
          <ul className="list-disc list-inside space-y-1">
            {message.map((msg, idx) => (
              <li key={idx} className="text-sm leading-relaxed">
                {msg}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm leading-relaxed">{message}</p>
        )}
      </div>

      {/* Bouton de fermeture moderne */}
      {onClose && (
        <button
          onClick={onClose}
          className="shrink-0 flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 
                     rounded transition-colors duration-200 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
          aria-label="Fermer l'alerte"
          title="Fermer"
        >
          <BsX className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default U2000Alert;
